import {
    Address,
    CustomsInformation,
    FuelType,
    MeansOfTransportType,
    ModelCategory,
    VehicleDimensions,
    VehicleType,
} from '../sharedComponents/common.types';

export enum CompoundManagementView {
    INCOMING_VEHICLES = 'outboundOrderBook.compoundManagement.toolbar.incoming',
    INVENTORY = 'outboundOrderBook.compoundManagement.toolbar.inventory',
    OUTGOING_VEHICLES = 'outboundOrderBook.compoundManagement.toolbar.outgoing',
    DEPARTED = 'outboundOrderBook.compoundManagement.toolbar.departed',
}

export type CompoundLocation = {
    readonly identifier: string;
    readonly address: Address;
    readonly timeZone: string;
};

export type FreightForwarder = {
    readonly name: string;
    readonly additionalName: string | undefined;
};

export type ExpectedArrival = {
    readonly expectedArrivalAt: Date;
    readonly transportInformation: TransportInformation;
    readonly freightForwarder: FreightForwarder | undefined;
    readonly arrivalFrom: Location | undefined;
    readonly isCancelled: boolean;
};

export type Arrival = {
    readonly arrivalCompletedAt: Date;
    readonly transportInformation: TransportInformation;
    readonly freightForwarder: FreightForwarder | undefined;
    readonly arrivalFrom: Location | undefined;
    readonly isCancelled: boolean;
    readonly isFinalized: boolean;
};

export type ExpectedDeparture = {
    readonly expectedDepartureAt: Date;
    readonly transportInformation: TransportInformation;
    readonly freightForwarder: FreightForwarder | undefined;
    readonly departureTo: Location | undefined;
    readonly isCancelled: boolean;
};

export type Departure = {
    readonly departureCompletedAt: Date;
    readonly transportInformation: TransportInformation;
    readonly freightForwarder: FreightForwarder | undefined;
    readonly departureTo: Location | undefined;
    readonly isFinalized: boolean;
    readonly isCancelled: boolean;
};

export type Model = {
    readonly name: string;
    readonly version: string | undefined;
    /* note: called modelGroup in transport assignment context, feel free to align if needed */
    readonly group: string;
    readonly manufacturer: string | undefined;
    readonly fuelType: FuelType | undefined;
    readonly category: ModelCategory | undefined;
};

export type Vehicle = {
    readonly vin: string;
    readonly model: Model;
    readonly vehicleType: VehicleType | undefined;
    readonly manufacturedAt: Date | undefined;
    readonly finalDestination: Location | undefined;
    readonly dimensions: VehicleDimensions | undefined;
    readonly weight: number | undefined;
    readonly colour: string | undefined;
    readonly licensePlate: string | undefined;
    readonly customsInformation: CustomsInformation | undefined;
};

export type Location = {
    identifier: string | undefined;
    address: Partial<Address> | undefined;
};

export type IncomingVehicle = {
    readonly id: string;
    readonly compoundLocation: CompoundLocation;
    readonly arrival: ExpectedArrival;
    readonly vehicle: Vehicle;
    readonly allowedActions: AllowedAction[];
};

export type InventoryVehicle = {
    readonly id: string;
    readonly compoundLocation: CompoundLocation;
    readonly arrival: Arrival;
    readonly vehicle: Vehicle;
    readonly allowedActions: AllowedAction[];
};

export type OutgoingVehicle = {
    readonly id: string;
    readonly compoundLocation: CompoundLocation;
    readonly departure: ExpectedDeparture;
    readonly vehicle: Vehicle;
    readonly allowedActions: AllowedAction[];
};

export type DepartedVehicle = {
    readonly id: string;
    readonly compoundLocation: CompoundLocation;
    readonly arrival: Arrival | undefined;
    readonly departure: Departure;
    readonly vehicle: Vehicle;
    readonly allowedActions: AllowedAction[];
};

export type CompoundVehicle = IncomingVehicle | InventoryVehicle | OutgoingVehicle | DepartedVehicle;

export type IncomingVehiclesPage = {
    readonly items: IncomingVehicle[];
    readonly hasMore: boolean;
};

export type InventoryVehiclesPage = {
    readonly items: InventoryVehicle[];
    readonly hasMore: boolean;
};

export type OutgoingVehiclesPage = {
    readonly items: OutgoingVehicle[];
    readonly hasMore: boolean;
};

export type DepartedVehiclesPage = {
    readonly items: DepartedVehicle[];
    readonly hasMore: boolean;
};

export type CompletionStatusChange = {
    readonly statusAt: Date;
    readonly transportInformation: TransportInformation;
    readonly compoundVehicleIds: string[];
};

export type TransportInformation = {
    readonly meansOfTransportType: MeansOfTransportType;
    readonly meansOfTransportId?: string | undefined;
    readonly meansOfTransportName?: string | undefined;
    readonly trailerId?: string | undefined;
    readonly journeyId?: string | undefined;
    readonly loadId?: string | undefined;
};
export type LoadValidationRequest = {
    ids: string[];
    vins: string[];
};

export enum AllowedAction {
    REPORT_ARRIVAL = 'reportArrival',
    REPORT_ANNOUNCED_DEPARTURE = 'reportAnnouncedDeparture',
    REPORT_UNANNOUNCED_DEPARTURE = 'reportUnannouncedDeparture',
}

export enum LoadPreviewErrorCode {
    UNKNOWN_VIN = 'unknownVin',
    INCORRECT_STATE = 'incorrectState',
    OTHER_SUPPLIER = 'otherSupplier',
    CANCELLED = 'cancelled',
    HAS_OUTGOING = 'hasOutgoing',
    DIFFERENT_MEANS_TYPE = 'differentMeansType',
    VEHICLE_IN_INCOMING = 'vehicleInIncoming',
    INVALID_LOCATION = 'invalidLocation',
    MISSING_PERMISSION = 'missingPermission',
}

export type VehicleInLoad = {
    readonly vin: string;
    readonly model: {
        readonly manufacturer: string | undefined;
        readonly name: string;
        readonly version: string | undefined;
    };
    readonly vehicleType: VehicleType | undefined;
    readonly finalDestination: Location | undefined;
};

export type LoadPreviewError = {
    errorCode: LoadPreviewErrorCode;
    vins: string[];
};

export type LoadPreviewUnannouncedDepartures = {
    readonly vehicles: VehicleInUnannouncedLoadPreview[];
    readonly invalidVins: LoadPreviewError[];
    readonly timeZone: string;
};

// Try to be structurally compatible with the IncomingVehicle
export type LoadPreviewArrivals = {
    readonly vehicles: IncomingVehicleInLoadPreview[];
    readonly invalidVins: LoadPreviewError[];
    readonly timeZone: string;
};

export type IncomingVehicleInLoadPreview = {
    readonly id: string;
    readonly vehicle: VehicleInLoad;
    readonly arrival: ExpectedArrivalLoadPreview;
};

// Try to be structurally compatible with the OutgoingVehicle
export type LoadPreviewDepartures = {
    readonly vehicles: OutgoingVehicleInLoadPreview[];
    readonly invalidVins: LoadPreviewError[];
    readonly timeZone: string;
};

export type OutgoingVehicleInLoadPreview = {
    readonly id: string;
    readonly vehicle: VehicleInLoad;
    readonly departure: ExpectedDepartureLoadPreview;
};

// Try to be structurally compatible with the InventoryVehicle
export type VehicleInUnannouncedLoadPreview = {
    readonly id: string;
    readonly vehicle: VehicleInLoad;
};

export type ExpectedArrivalLoadPreview = {
    readonly transportInformation: TransportInformationLoadPreview;
};

export type ExpectedDepartureLoadPreview = {
    readonly transportInformation: TransportInformationLoadPreview;
};

export type TransportInformationLoadPreview = {
    readonly meansOfTransportType: MeansOfTransportType;
    readonly meansOfTransportId?: string | undefined;
    readonly trailerId?: string | undefined;
};
